import { useEffect, useMemo, useState } from "react";
import { Gauge, Loader2, RefreshCw, Settings2, History, X, Save, Search } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, formatDateID } from "@/lib/billingMeta";

const READING_LABELS = { initial: "Awal", periodic: "Berkala", final: "Akhir (checkout)" };

function MeterModal({ room, onClose, onDone }) {
  const [meterNumber, setMeterNumber] = useState(room.meter_number || "");
  const [meterCurrent, setMeterCurrent] = useState(room.meter_current ?? "");
  const [ppk, setPpk] = useState(room.price_per_kwh ?? "");
  const [busy, setBusy] = useState(false);
  const initialized = room.meter_initialized;

  const submit = async () => {
    if (!initialized && meterCurrent === "") { toast.error("Isi meteran awal."); return; }
    if (ppk === "") { toast.error("Isi harga per kWh."); return; }
    setBusy(true);
    try {
      await apiClient.post(`/rooms/${room.room_id}/meter/init`, {
        meter_number: meterNumber || null,
        meter_current: Number(meterCurrent || room.meter_current || 0),
        price_per_kwh: Number(ppk),
      });
      toast.success("Meteran disimpan.");
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="meter-modal" onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="font-heading text-lg font-semibold">Atur Meteran · Kamar {room.room_number}</h3>
          <button onClick={onClose} data-testid="meter-modal-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Nomor Meter</label>
            <input value={meterNumber} onChange={(e) => setMeterNumber(e.target.value)} data-testid="meter-number-input"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Meteran Awal (kWh){initialized ? " — sudah diset, tidak dapat diubah" : ""}</label>
            <input type="number" value={meterCurrent} disabled={initialized} onChange={(e) => setMeterCurrent(e.target.value)} data-testid="meter-current-input"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60" />
            {initialized && <p className="text-xs text-muted-foreground mt-1">Meteran berjalan mengikuti billing yang dipublish secara otomatis.</p>}
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Harga per kWh</label>
            <input type="number" value={ppk} onChange={(e) => setPpk(e.target.value)} data-testid="meter-ppk-input"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <button onClick={submit} disabled={busy} data-testid="meter-save"
            className="w-full py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ room, onClose }) {
  const [readings, setReadings] = useState(null);
  useEffect(() => {
    apiClient.get(`/rooms/${room.room_id}/meter`).then((r) => setReadings(r.data.readings || [])).catch(() => setReadings([]));
  }, [room.room_id]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="meter-history-modal" onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="font-heading text-lg font-semibold">Riwayat Meteran · Kamar {room.room_number}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
        </div>
        <div className="p-5">
          {readings === null ? <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-primary" /></div> :
            readings.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada pembacaan meteran.</p> :
            <div className="space-y-2">
              {readings.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm border-b border-border py-2">
                  <div>
                    <p className="font-semibold">{r.kwh_value} kWh <span className="text-xs font-normal text-muted-foreground">· {READING_LABELS[r.reading_type] || r.reading_type}</span></p>
                    {r.note && <p className="text-xs text-muted-foreground">{r.note}</p>}
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDateID(r.reading_date)}</span>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}

export default function OwnerMetersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRoom, setEditRoom] = useState(null);
  const [histRoom, setHistRoom] = useState(null);
  const [propId, setPropId] = useState("all");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("room-asc");

  const load = () => {
    setLoading(true);
    apiClient.get("/owner/meters").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const properties = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.property_id) m.set(r.property_id, r.property_name || "Properti"); });
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (propId !== "all" && r.property_id !== propId) return false;
      if (status === "set" && !r.meter_initialized) return false;
      if (status === "unset" && r.meter_initialized) return false;
      if (term && !String(r.room_number || "").toLowerCase().includes(term) && !String(r.meter_number || "").toLowerCase().includes(term)) return false;
      return true;
    });
    const rn = (r) => String(r.room_number || "");
    const cmp = {
      "room-asc": (a, b) => rn(a).localeCompare(rn(b), undefined, { numeric: true }),
      "room-desc": (a, b) => rn(b).localeCompare(rn(a), undefined, { numeric: true }),
      "property-asc": (a, b) => String(a.property_name || "").localeCompare(String(b.property_name || "")) || rn(a).localeCompare(rn(b), undefined, { numeric: true }),
      "meter-desc": (a, b) => (b.meter_current || 0) - (a.meter_current || 0),
      "price-desc": (a, b) => (b.price_per_kwh || 0) - (a.price_per_kwh || 0),
    }[sort];
    return cmp ? [...out].sort(cmp) : out;
  }, [rows, propId, q, status, sort]);

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Gauge size={22} className="text-primary" /> Meteran Listrik</h1>
          <p className="text-sm text-muted-foreground">Meteran milik kamar. Set meteran awal sekali; nilai berjalan mengikuti billing.</p>
        </div>
        <button onClick={load} data-testid="meters-refresh" className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-3 mb-5" data-testid="meters-filters">
        <select value={propId} onChange={(e) => setPropId(e.target.value)} data-testid="meters-property-filter"
          className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
          <option value="all">Semua Properti</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="meters-status-filter"
          className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
          <option value="all">Semua Status</option>
          <option value="set">Terset</option>
          <option value="unset">Belum</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="meters-sort"
          className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
          <option value="room-asc">Kamar (A→Z)</option>
          <option value="room-desc">Kamar (Z→A)</option>
          <option value="property-asc">Properti (A→Z)</option>
          <option value="meter-desc">Meteran terbesar</option>
          <option value="price-desc">Harga/kWh tertinggi</option>
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="meters-search" placeholder="Cari kamar / no. meter"
            className="w-full rounded-xl border border-border bg-secondary/40 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
        </div>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" data-testid="meters-empty"><Gauge size={30} className="mx-auto mb-2" /> Belum ada kamar.</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" data-testid="meters-no-match"><Gauge size={30} className="mx-auto mb-2" /> Tidak ada kamar yang cocok dengan filter.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border" data-testid="meters-table">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-secondary/50 text-left">
              <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:text-muted-foreground">
                <th>Properti</th><th>Kamar</th><th>No. Meter</th><th>Meteran Berjalan</th><th>Harga/kWh</th><th>Status</th><th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.room_id} data-testid={`meter-row-${i}`} className="border-t border-border [&>td]:px-4 [&>td]:py-3">
                  <td>{r.property_name || "-"}</td>
                  <td className="font-semibold">{r.room_number}</td>
                  <td>{r.meter_number || "-"}</td>
                  <td>{r.meter_current != null ? `${r.meter_current} kWh` : "-"}</td>
                  <td>{r.price_per_kwh != null ? formatRupiah(r.price_per_kwh) : "-"}</td>
                  <td>{r.meter_initialized ? <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-emerald-200 bg-emerald-100 text-emerald-700">Terset</span> : <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">Belum</span>}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => setEditRoom(r)} data-testid={`meter-edit-${i}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60"><Settings2 size={14} /> Atur</button>
                      <button onClick={() => setHistRoom(r)} data-testid={`meter-history-${i}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60"><History size={14} /> Riwayat</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editRoom && <MeterModal room={editRoom} onClose={() => setEditRoom(null)} onDone={load} />}
      {histRoom && <HistoryModal room={histRoom} onClose={() => setHistRoom(null)} />}
    </OwnerLayout>
  );
}
